import { useQuery } from "@tanstack/react-query";
import { apiGet, apiGetBlob } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";
import type { ThreeWayBasis } from "../../../../components/PortfolioPerformance/basis-toggle";

/** Same sentinel role as risk-pricing-engine's own ALL_RATINGS_LABEL --
 *  omitted from the request entirely rather than sent as a literal filter
 *  value, since the backend has no "All Ratings" row to match against. */
export const ALL_RATINGS_LABEL = "All Ratings";

/** One point per file_date -- same source/shape as risk-pricing-engine's own
 *  AnnualizedLossTrendPoint. */
export interface LossCalibrationTrendPoint {
  fileDate: string;
  lifetimeLoss: number;
  annualizedLossPct: number;
}

/** Same tree shape risk-pricing-engine's own lib/api/annualized-loss.ts
 *  defines -- both read the same source table
 *  (athena_bfsi_rbp.annualized_loss_calibiration), see this app's
 *  app/repository/annualized_loss_calibration.py. */
export interface LossCalibrationIndustryRow {
  key: string;
  label: string;
  maturedContracts: number;
  financedAmount: number;
  lifetimeLoss: number;
  walYears: number | null;
  loanToLifeLossPct: number | null;
  annualizedLossPct: number | null;
}

export interface LossCalibrationOfficeRow extends LossCalibrationIndustryRow {
  industries: LossCalibrationIndustryRow[];
}

export interface LossCalibrationRatingRow extends LossCalibrationIndustryRow {
  offices: LossCalibrationOfficeRow[];
}

export interface LossCalibrationOfficeLeafRow extends LossCalibrationIndustryRow {}

export interface LossCalibrationRatingGroupRow extends LossCalibrationIndustryRow {
  offices: LossCalibrationOfficeLeafRow[];
}

export interface LossCalibrationIndustryGroupRow extends LossCalibrationIndustryRow {
  ratings: LossCalibrationRatingGroupRow[];
}

export interface AnnualizedLossCalibrationResponse {
  trend: LossCalibrationTrendPoint[];
  rows: LossCalibrationRatingRow[];
  rowsByIndustry: LossCalibrationIndustryGroupRow[];
}

/** Streams every contract behind the two grouped tables (one row per
 *  contract) under the page's current Industry / Office / Rating filters.
 *  CORS hides Content-Disposition cross-origin, so the filename is fixed here. */
export async function downloadAnnualizedLossContracts(
  industry?: string,
  officeNumber?: number,
  rating?: string,
  ticket?: TicketSize,
): Promise<void> {
  const blob = await apiGetBlob("/annualized-loss/download/contracts", {
    ticket,
    industry,
    office: officeNumber != null ? String(officeNumber) : undefined,
    rating: rating && rating !== ALL_RATINGS_LABEL ? rating : undefined,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "annualized_loss_contracts.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** `officeNumber` (not office name) matches risk-pricing-engine's own filter
 *  convention -- the page resolves the selected office name to its number
 *  before calling this. `basis` picks the rows the trend and both tables read:
 *  realized, projected, or of cohorts that still have active contracts. */
export function useAnnualizedLossCalibration(
  industry?: string,
  officeNumber?: number,
  rating?: string,
  basis: ThreeWayBasis = "realized",
) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: [
      "annualized-loss-calibration",
      industry ?? "all",
      officeNumber ?? "all",
      rating ?? ALL_RATINGS_LABEL,
      basis,
      ticket ?? "all",
    ],
    queryFn: () =>
      apiGet<AnnualizedLossCalibrationResponse>("/annualized-loss/calibration", {
        ...(industry ? { industry } : {}),
        ...(officeNumber != null ? { office: String(officeNumber) } : {}),
        ...(rating && rating !== ALL_RATINGS_LABEL ? { rating } : {}),
        ...(ticket ? { ticket } : {}),
        basis,
      }),
    staleTime: 60_000,
  });
}
